// "E-Mail korrigieren" / "Telefonnummer korrigieren" (ST-PTY-002, ADR-0043
// Nachtrag). Ein Tippfehler wird am Kontaktweg selbst berichtigt und gilt fuer
// alle Zwecke; eine andere Adresse oder Nummer wird beendet und neu erfasst.
//
// Eine zurueckhaltende Aktion je Kontaktweg; das Formular erscheint erst auf
// Anforderung, vorbefuellt, der Cursor am Ende (ein Zeichen aendern, nicht
// alles neu tippen). Abbrechen und Escape geben den Fokus an den Oeffner
// zurueck (WCAG 2.4.3). Ist die Eingabe der bisherige Wert, steht der Hinweis
// am Feld, ohne dass etwas gesendet wird.
//
// Sperren am Weg stehen als Hinweis ueber dem Feld. Nur bei "Nicht zustellbar",
// die der Abschnitt Sperren aufheben wuerde, gibt es ein vorausgewaehltes
// Haekchen: eine betriebliche Wahl, keine Einwilligung.

import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Body1, Button, Caption1, Checkbox, Field, Input } from '@fluentui/react-components';

import { CORRECTION_LIFT_LABEL, correctionNoun, CORRECTION_UNCHANGED_HINT, isUnchangedCorrection } from '../contact-points.ts';
import type { FieldError } from '../command-client.ts';
import { validationMessageFor } from '../quick-capture.ts';
import { usePflegeStyles } from '../shell/pflegeabschnitt.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

export function KontaktwegKorrigieren({
  channel,
  value,
  zwecke,
  sperrHinweise,
  aufhebenAngeboten,
  offen,
  busy,
  zuruecktreten,
  feldfehler,
  onOeffnen,
  onAbbrechen,
  onSpeichern,
}: {
  readonly channel: 'email' | 'phone';
  /** Der gespeicherte Wert: Vorbelegung und Teil des zugaenglichen Namens. */
  readonly value: string;
  /** Die Zwecke dieses Kontaktwegs in Worten; ab zwei nennt die Erklaerung sie. */
  readonly zwecke: readonly string[];
  /** Je Sperre an diesem Weg ein Satz (correctionSuppressionNote). */
  readonly sperrHinweise: readonly string[];
  /** Ob das Haekchen "Nicht zustellbar ebenfalls aufheben" angeboten wird. */
  readonly aufhebenAngeboten: boolean;
  readonly offen: boolean;
  readonly busy: boolean;
  /** Solange "Erneut versuchen" steht, ist das die eine Hauptaktion (NFR-UX-004). */
  readonly zuruecktreten: boolean;
  /** Die Feldantwort des Servers, nur solange dieses Formular offen ist. */
  readonly feldfehler: readonly FieldError[];
  readonly onOeffnen: () => void;
  readonly onAbbrechen: () => void;
  readonly onSpeichern: (eingabe: string, sperreAufheben: boolean) => void;
}): ReactElement {
  const pflege = usePflegeStyles();
  const titelId = useId();
  const nomen = correctionNoun(channel);
  const [eingabe, setEingabe] = useState(value);
  const [unveraendert, setUnveraendert] = useState(false);
  const [aufheben, setAufheben] = useState(true);
  const panelRef = useRef<HTMLFormElement | null>(null);
  const knopfRef = useRef<HTMLDivElement | null>(null);
  const feld = (): HTMLInputElement | null => panelRef.current?.querySelector<HTMLInputElement>('input') ?? null;
  const warOffen = useRef(false);
  const abgebrochen = useRef(false);
  useEffect(() => {
    if (offen && !warOffen.current) {
      // Jedes Oeffnen beginnt mit dem GESPEICHERTEN Wert, nicht mit einem verworfenen Entwurf.
      setEingabe(value);
      setUnveraendert(false);
      setAufheben(true);
      const eingabefeld = feld();
      if (eingabefeld !== null) {
        eingabefeld.focus();
        try {
          eingabefeld.setSelectionRange(eingabefeld.value.length, eingabefeld.value.length);
        } catch {
          // type="email" kennt keine Auswahl; dort setzt der Browser den Cursor.
        }
      }
    } else if (!offen && warOffen.current && abgebrochen.current) {
      knopfRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
    warOffen.current = offen;
    abgebrochen.current = false;
  }, [offen, value]);

  const abbrechen = (): void => {
    abgebrochen.current = true;
    onAbbrechen();
  };

  if (!offen) {
    return (
      <div className={pflege.zeilenaktionen} ref={knopfRef}>
        {/* Der sichtbare Text steht VORN im Namen (WCAG 2.5.3). */}
        <Button
          appearance="secondary"
          size="small"
          className={pflege.zeilenknopf}
          disabled={busy}
          aria-label={`${nomen} korrigieren: ${value}`}
          onClick={onOeffnen}
        >
          {nomen} korrigieren
        </Button>
      </div>
    );
  }

  const serverFehler = feldfehler.find((eintrag) => eintrag.fieldPath === 'value');
  const meldung = unveraendert
    ? CORRECTION_UNCHANGED_HINT
    : serverFehler === undefined
      ? null
      : validationMessageFor(channel, serverFehler.reasonCode).hint;
  const ding = channel === 'phone' ? 'Nummer' : 'Adresse';

  // Die Gruppe ist ein div um das Formular: role="group" am <form> meldet axe (aria-allowed-role).
  return (
    <div role="group" aria-labelledby={titelId}>
      <form
        className={pflege.beenden}
        ref={panelRef}
        noValidate
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            abbrechen();
          }
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (isUnchangedCorrection(value, eingabe)) {
            setUnveraendert(true);
            feld()?.focus();
            return;
          }
          onSpeichern(eingabe, aufhebenAngeboten && aufheben);
        }}
      >
        <Body1 id={titelId}>
          <strong>{nomen} korrigieren</strong>
        </Body1>
        <Caption1>
          Für einen Tippfehler in dieser {ding}.
          {zwecke.length > 1 ? ` Die Korrektur gilt für alle Zwecke (${zwecke.join(', ')}).` : ''} Ist es eine andere {ding}, setze
          diese auf „Nicht mehr verwenden“ und trage die neue mit „Kontaktweg hinzufügen“ ein.
        </Caption1>
        {sperrHinweise.map((hinweis) => (
          <Caption1 key={hinweis}>{hinweis}</Caption1>
        ))}
        <Field label={channel === 'email' ? 'E-Mail-Adresse' : 'Telefonnummer'} required validationMessage={meldung}>
          <Input
            className={pflege.feld}
            type={channel === 'email' ? 'email' : 'tel'}
            autoComplete={channel === 'email' ? 'email' : 'tel'}
            value={eingabe}
            onChange={(_event, data) => {
              setEingabe(data.value);
              setUnveraendert(false);
            }}
          />
        </Field>
        {aufhebenAngeboten ? (
          <Checkbox
            label={CORRECTION_LIFT_LABEL}
            checked={aufheben}
            disabled={busy}
            onChange={(_event, data) => setAufheben(data.checked === true)}
          />
        ) : undefined}
        <div className={pflege.zeilenaktionen}>
          <Button appearance={zuruecktreten ? 'secondary' : 'primary'} disabled={busy} type="submit">
            {busy ? 'Wird gespeichert …' : 'Korrektur speichern'}
          </Button>
          <NebenKnopf disabled={busy} onClick={abbrechen}>
            Abbrechen
          </NebenKnopf>
        </div>
      </form>
    </div>
  );
}
